/**
 * LandlordCard — auto-detect + confirm the user's landlord.
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="landlord-card"></div>
 *
 * Required Heyflow fields (same as the address card):
 *   - Address (street), Address (house), Address (zip) — or "Full address".
 *
 * Reads from sessionStorage:
 *   STORAGE_KEYS.street, .house, .zip, .address
 *
 * Writes to sessionStorage (consumed by the looks-good card and the funnel
 * itself when submitting the mandate):
 *   conny_landlord_category       "private" | "commercial" | "not_sure"
 *   conny_landlord_known_key      e.g. "covivio" / "deutsche_wohnen" / …
 *   conny_landlord_raw            human-readable landlord name
 *   conny_landlord_type           "private" | "other_corporate" | "known_corporate"
 *   conny_landlord_user_selected  "true" once the user has confirmed
 *   landlord / landlord_type / conny_landlord / conny_landlord_name
 *     — extra aliases so existing Heyflow funnels can read the choice back.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { getFunnelContext, listLandlords, Landlord } from "../../shared/api";
import { readAddress } from "../../shared/address";
import { CheckIcon, PinIcon } from "../../shared/icons";
import {
  removeStorage,
  STORAGE_KEYS,
  useStorage,
  writeStorage,
} from "../../shared/storage";

interface Selection {
  category: "" | "private" | "commercial" | "not_sure";
  knownKey: string;
  raw: string;
  type: string;
  userSelected: boolean;
}

function saveSelection(
  category: Selection["category"],
  knownKey: string,
  userSelected: boolean,
  byKey: Record<string, Landlord>,
  addressKey: string = "",
) {
  let raw = "";
  let type = "";
  let key = knownKey;

  if (category === "private") {
    raw = "Private landlord";
    type = "private";
    key = "";
  } else if (category === "commercial") {
    if (key && byKey[key]) {
      raw = byKey[key].name;
      type = byKey[key].type || "other_corporate";
    } else if (key === "other_commercial") {
      raw = "Other commercial landlord";
      type = "other_corporate";
    } else {
      // Empty key = "Commercial" picked at the top level but no sub-choice
      // yet. Keep raw/type empty so we don't claim a specific landlord.
      key = "";
      raw = "";
      type = "other_corporate";
    }
  } else if (category === "not_sure") {
    raw = "Not sure";
    type = "";
    key = "";
  }

  writeStorage(STORAGE_KEYS.landlordCategory, category);
  writeStorage(STORAGE_KEYS.landlordKnownKey, key);
  writeStorage(STORAGE_KEYS.landlordRaw, raw);
  writeStorage(STORAGE_KEYS.landlordType, type);
  writeStorage("landlord", raw);
  writeStorage("landlord_type", type);
  writeStorage("conny_landlord", raw);
  writeStorage("conny_landlord_name", raw);

  if (userSelected) {
    writeStorage(STORAGE_KEYS.landlordUserSelected, "true");
    removeStorage(STORAGE_KEYS.landlordAutoSelected);
  } else {
    writeStorage(STORAGE_KEYS.landlordAutoSelected, "true");
  }
  writeStorage(STORAGE_KEYS.landlordSelectedForAddress, addressKey);

  window.dispatchEvent(
    new CustomEvent("conny-landlord-selection-updated", {
      detail: {
        landlord: raw,
        landlordType: type,
        category,
        knownKey: key,
        userSelected,
      },
    }),
  );
}

function useReactiveAddress() {
  useStorage(STORAGE_KEYS.street);
  useStorage(STORAGE_KEYS.house);
  useStorage(STORAGE_KEYS.zip);
  useStorage(STORAGE_KEYS.address);
  return readAddress();
}

export function LandlordCard() {
  const [category] = useStorage(STORAGE_KEYS.landlordCategory);
  const [knownKey] = useStorage(STORAGE_KEYS.landlordKnownKey);
  const [userSelectedStr] = useStorage(STORAGE_KEYS.landlordUserSelected);
  const [autoSelectedStr] = useStorage(STORAGE_KEYS.landlordAutoSelected);
  const [selectedForAddress] = useStorage(
    STORAGE_KEYS.landlordSelectedForAddress,
  );
  const userSelected = userSelectedStr === "true";
  // `autoSelected` kept for backwards compat (legacy storage key still written).
  void autoSelectedStr;
  const addr = useReactiveAddress();
  const addressKey = `${addr.street}|${addr.houseNumber}|${addr.zip}`;

  const landlordsQuery = useQuery({
    queryKey: ["landlords"],
    queryFn: () => listLandlords(),
    staleTime: 30 * 60_000,
  });
  const landlords = landlordsQuery.data ?? [];

  const byKey = useMemo<Record<string, Landlord>>(() => {
    const m: Record<string, Landlord> = {};
    for (const l of landlords) m[l.key] = l;
    return m;
  }, [landlords]);

  const ctxQuery = useQuery({
    queryKey: ["funnel-context", addr.street, addr.houseNumber, addr.zip],
    queryFn: () =>
      getFunnelContext({
        street: addr.street,
        house: addr.houseNumber,
        zip: addr.zip,
      }),
    enabled: Boolean(addr.street && addr.houseNumber && addr.zip),
    staleTime: 5 * 60_000,
  });
  const detected = ctxQuery.data?.landlord ?? null;

  // Always merge the detected landlord into the keyed map so saveSelection()
  // resolves its name/type even when it isn't in the top-N listLandlords().
  const byKeyWithDetected = useMemo<Record<string, Landlord>>(() => {
    if (!detected) return byKey;
    return { ...byKey, [detected.key]: detected };
  }, [byKey, detected]);

  // Auto-select the detected landlord ONLY when nothing has been committed
  // for this exact address. As soon as the user clicks anything (private,
  // commercial, a specific commercial landlord, …) we record their choice
  // against this address and never overwrite it again until the address
  // itself changes.
  //
  // The check on `knownKey !== detected.key` prevents an infinite render loop
  // on the initial auto-select.
  useEffect(() => {
    if (!detected) return;
    const committedForThisAddress = selectedForAddress === addressKey;
    if (committedForThisAddress) return;
    if (knownKey === detected.key && category === "commercial") return;
    saveSelection(
      "commercial",
      detected.key,
      false,
      byKeyWithDetected,
      addressKey,
    );
  }, [
    detected,
    knownKey,
    category,
    byKeyWithDetected,
    selectedForAddress,
    addressKey,
  ]);

  const selection: Selection = {
    category: (category as Selection["category"]) || "",
    knownKey,
    raw: "",
    type: "",
    userSelected,
  };

  // The detected landlord is shown only in the top "Likely landlord
  // identified" box. The commercial sub-panel below appears only when the
  // user explicitly picks "Commercial landlord" (and never lists the
  // detected one as a duplicate).
  const detectedSelected =
    Boolean(detected) &&
    selection.category === "commercial" &&
    selection.knownKey === detected!.key;
  const showCommercialPanel =
    selection.category === "commercial" && !detectedSelected;

  return (
    <div className="cn">
      <div className="cn-card cn-select">
        {detected && (
          <DetectedOption
            landlord={detected}
            selected={detectedSelected}
            onClick={() =>
              saveSelection(
                "commercial",
                detected.key,
                true,
                byKeyWithDetected,
                addressKey,
              )
            }
          />
        )}

        <div
          className="cn-options"
          role="radiogroup"
          aria-label="Landlord type"
        >
          <Option
            title="Private landlord"
            copy="A private person rents out the apartment."
            selected={selection.category === "private"}
            onClick={() =>
              saveSelection("private", "", true, byKey, addressKey)
            }
          />
          <Option
            title="Commercial landlord"
            copy="A company or housing provider is your landlord."
            selected={selection.category === "commercial" && !detectedSelected}
            onClick={() => {
              // Clicking the parent "Commercial landlord" radio means "I want
              // to pick a different commercial landlord than the auto-detected
              // one". We open the sub-panel with NO sub-item preselected
              // (knownKey = "") so the user can choose.
              //
              // If we don't have a detected landlord, fall back to the
              // existing knownKey so re-clicking "Commercial" is a no-op.
              const preselect = detected ? "" : selection.knownKey || "";
              saveSelection(
                "commercial",
                preselect,
                true,
                byKeyWithDetected,
                addressKey,
              );
            }}
          />

          {showCommercialPanel && (
            <div className="cn-sub-panel">
              <div className="cn-sub-panel-title">
                Which commercial landlord?
              </div>
              <div
                className="cn-options"
                role="radiogroup"
                aria-label="Commercial landlord"
              >
                {(() => {
                  const list = landlords
                    .filter((l) => l.key !== "other_commercial")
                    .slice();
                  // Make sure the detected landlord is in the list even if
                  // it isn't part of the top-N returned by listLandlords().
                  if (detected && !list.some((l) => l.key === detected.key)) {
                    list.push(detected);
                  }
                  list.sort((a, b) => {
                    // Detected one always first, then by count desc.
                    if (detected) {
                      if (a.key === detected.key) return -1;
                      if (b.key === detected.key) return 1;
                    }
                    return (b.count || 0) - (a.count || 0);
                  });
                  return list.map((l) => (
                    <Option
                      key={l.key}
                      title={l.name}
                      copy={
                        l.count
                          ? `${l.count.toLocaleString("en-US")} cases already`
                          : "Known commercial landlord"
                      }
                      selected={selection.knownKey === l.key}
                      onClick={() =>
                        saveSelection(
                          "commercial",
                          l.key,
                          true,
                          byKeyWithDetected,
                          addressKey,
                        )
                      }
                      variant="sub"
                      autoDetected={
                        Boolean(detected) && l.key === detected!.key
                      }
                    />
                  ));
                })()}
                <Option
                  title="Other commercial landlord"
                  copy="A different housing company or commercial owner."
                  selected={selection.knownKey === "other_commercial"}
                  onClick={() =>
                    saveSelection(
                      "commercial",
                      "other_commercial",
                      true,
                      byKeyWithDetected,
                      addressKey,
                    )
                  }
                  variant="sub"
                />
              </div>
            </div>
          )}

          <Option
            title="Not sure"
            copy="That is fine — we can still check your case."
            selected={selection.category === "not_sure"}
            onClick={() =>
              saveSelection("not_sure", "", true, byKey, addressKey)
            }
          />
        </div>

        <div className="cn-reassurance">
          <span className="cn-row-left">
            <span className="cn-check">
              <CheckIcon />
            </span>
            <span className="cn-reassurance-text">
              Conny only contacts your landlord after you mandate us via email.
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

function Option({
  title,
  copy,
  selected,
  onClick,
  variant = "main",
  autoDetected = false,
}: {
  title: string;
  copy: string;
  selected: boolean;
  onClick: () => void;
  variant?: "main" | "sub";
  autoDetected?: boolean;
}) {
  const cls = variant === "sub" ? "cn-option--sub" : "cn-option";
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`${cls} ${selected ? "is-selected" : ""}`}
      onClick={onClick}
    >
      <span className="cn-radio" aria-hidden="true" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="cn-opt-title">
          {title}
          {autoDetected && <span className="cn-auto-badge">Detected</span>}
        </span>
        <span className="cn-opt-copy">{copy}</span>
      </span>
    </button>
  );
}

/**
 * The "Likely landlord identified" box at the top of the card. It looks like
 * the blue banner from the original mock-up but behaves as a radio: clicking
 * it (or it being preselected) saves the detected landlord as the answer.
 */
function DetectedOption({
  landlord,
  selected,
  onClick,
}: {
  landlord: Landlord;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`cn-detected ${selected ? "is-selected" : ""}`}
      onClick={onClick}
    >
      <span className="cn-radio" aria-hidden="true" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div className="cn-detected-top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cn-detected-label">
              <PinIcon /> <span>Likely landlord identified</span>
            </div>
            <div className="cn-detected-name">{landlord.name}</div>
          </div>
          <span className="cn-chip">
            {landlord.count
              ? `${landlord.count.toLocaleString("en-US")} cases already`
              : "Matched"}
          </span>
        </div>
        <div className="cn-detected-copy">
          {landlord.count
            ? `Good news — Conny has already handled ${landlord.count.toLocaleString("en-US")} cases against ${landlord.name}. If that's your landlord, keep it selected.`
            : `Based on your address, we identified your landlord as ${landlord.name}. If that's correct, keep it selected.`}
        </div>
      </span>
    </button>
  );
}
